import isNil from "lodash/isNil";
import isPlainObject from "lodash/isPlainObject";

import {
  createDateTimeFormatter,
  roundToTwoDecimals,
} from "components/helpers";

/** A multi-select custom field stores a list; any other value prints as-is. */
export const formatCustomFieldValue = value =>
  Array.isArray(value) ? value.join(", ") : value;

// Swoogo stores a choice answer as {id, value} and a multiple choice as a list
// of them. A list without any filled item, or a choice without a value, is empty.
const formatChoice = value => {
  if (Array.isArray(value)) {
    const texts = value
      .map(formatChoice)
      .filter(text => !isNil(text) && text !== "");
    return texts.length ? texts.join(", ") : null;
  }
  return isPlainObject(value) && "id" in value && "value" in value
    ? value.value
    : value;
};

export const CUSTOM_FIELD_CHANGES = {
  label: "custom field",
  mapValue: formatCustomFieldValue,
};

export function flattenChanges(changes, attributes = {}) {
  return (changes || []).flatMap(
    ({
      attribute,
      value,
      previous_value: prevValue,
      type,
      added = [],
      removed = [],
      modified = [],
      deleted = [],
    }) => {
      const options = attributes[attribute] || {};
      const label = options.label || attribute?.replace(/_/g, " ");
      // The backend names an unreadable column (JSON, editor state, a bare
      // id) without its values.
      if (
        value === undefined &&
        prevValue === undefined &&
        ![added, removed, modified, deleted].some(members => members.length)
      ) {
        return [`Changed ${label}`];
      }
      const valueMapper = value => {
        value = formatChoice(value);
        if (!value) {
          return value;
        }
        if (type === "DateTime") {
          value = createDateTimeFormatter({ timeZone: value.timezone })(
            value.isodate
          );
        }
        if (
          value["new_field"] &&
          value["original_field"] &&
          !value["new_value"]
        ) {
          value = `the key of custom field from '${value.original_field}' to '${value.new_field}' `;
        }
        if (
          value["field"] &&
          (value["new_value"] || value["new_value"] === false) &&
          (value["original_value"] || value["original_value"] === false)
        ) {
          value = `the value of '${value["field"]}' from '${value.original_value}' to '${value.new_value}'`;
        }
        if (value["deleted_field"]) {
          value = `'${value["deleted_field"]}'`;
        }
        const mapper = options.mapValue;
        return mapper ? mapper(value) : value;
      };
      const isEitherSideStored = !isNil(prevValue) || !isNil(value);
      prevValue = valueMapper(prevValue);
      value = valueMapper(value);
      // An empty answer replacing nothing, as Swoogo sends for an unanswered
      // choice, is not a change worth a line.
      if (isEitherSideStored && isNil(prevValue) && isNil(value)) {
        return [];
      }
      const action =
        !isNil(prevValue) && !isNil(value) && prevValue !== value
          ? "Changed"
          : !isNil(value)
            ? "Set"
            : "Unset";

      const line = ({ action, label, prevValue, value, addRemove }) => {
        const shownValue = roundToTwoDecimals(value);
        const shownPrevValue = roundToTwoDecimals(prevValue);
        return addRemove
          ? `${action} ${label} '${shownValue}'`
          : modified.length > 0
            ? `${action} ${shownValue}`
            : `${action} ${label} ${
                !isNil(prevValue) ? `from '${shownPrevValue}' ` : ""
              }${!isNil(value) ? `to '${shownValue}'` : ""}`;
      };

      let changes = [
        ...(value !== undefined ? [{ prevValue, value, action, label }] : []),
        ...added.map(value => ({
          action: "Added",
          addRemove: true,
          label,
          value: valueMapper(value),
        })),
        ...removed.map(value => ({
          action: "Removed",
          addRemove: true,
          label,
          value: valueMapper(value),
        })),
        ...modified.map(value => ({
          action: "Modified",
          modified: true,
          label,
          value: valueMapper(value),
        })),
        ...deleted.map(value => ({
          action: "Deleted",
          label,
          value: valueMapper(value),
        })),
      ];
      changes = changes.map(opts => {
        const mapper = options.mapLine;
        return mapper ? mapper(opts) : line(opts);
      });
      return changes;
    }
  );
}
